import React from 'react';
import { Card, CardContent, Typography, Chip, Box, Divider, useTheme } from '@mui/material';
import { AirRoute } from '../../types';

interface AirRouteCardProps {
  route: AirRoute;
  onClick: (route: AirRoute) => void;
  isSelected: boolean;
}

const AirRouteCard: React.FC<AirRouteCardProps> = ({ route, onClick, isSelected }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'commercial':
        return { color: 'success' as const, label: '工业化生产' };
      case 'pilot':
        return { color: 'warning' as const, label: '中试阶段' };
      case 'research':
        return { color: 'info' as const, label: '研发中' };
      default:
        return { color: 'default' as const, label: status };
    }
  };

  const statusConfig = getStatusConfig(route.status);

  return (
    <Card
      onClick={() => onClick(route)}
      sx={{
        cursor: 'pointer',
        height: '100%',
        border: isSelected 
          ? '3px solid' 
          : '1px solid',
        borderColor: isSelected 
          ? (isDarkMode ? '#66bb6a' : '#2e7d32')
          : (isDarkMode ? '#444444' : '#e0e0e0'),
        backgroundColor: isSelected 
          ? (isDarkMode ? '#1b5e20' : '#f1f8e9')
          : (isDarkMode ? '#1e1e1e' : '#ffffff'),
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: isDarkMode ? '#81c784' : '#4caf50',
          backgroundColor: isDarkMode ? '#2e7d32' : '#f9fbe7',
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              color: isDarkMode ? '#66bb6a' : '#2e7d32', 
              fontWeight: 600 
            }}
          >
            {route.name}
          </Typography>
          <Chip
            label={statusConfig.label}
            color={statusConfig.color}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
          {route.description}
        </Typography>

        <Divider sx={{ my: 1.5, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            原料:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {route.feedstock.map((item) => (
              <Chip key={item} label={item} size="small" color="primary" variant="outlined" />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            产品:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {route.products.map((item) => (
              <Chip key={item} label={item} size="small" color="success" variant="outlined" />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            副产品:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {route.byproducts.map((item) => (
              <Chip key={item} label={item} size="small" color="default" variant="outlined" />
            ))}
          </Box>
        </Box>

        {(route.temperature || route.pressure || route.purity || route.energyConsumption) && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              工艺参数:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {route.temperature && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: isDarkMode ? '#ffb74d' : '#ff9800',
                    fontSize: '0.85rem',
                  }}
                >
                  温度: {route.temperature}
                </Typography>
              )}
              {route.pressure && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: isDarkMode ? '#81d4fa' : '#0288d1',
                    fontSize: '0.85rem',
                  }}
                >
                  压力: {route.pressure}
                </Typography>
              )}
              {route.purity && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: isDarkMode ? '#ce93d8' : '#9c27b0',
                    fontSize: '0.85rem',
                  }}
                >
                  纯度: {route.purity}
                </Typography>
              )}
              {route.energyConsumption && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: isDarkMode ? '#66bb6a' : '#2e7d32',
                    fontSize: '0.85rem',
                  }}
                >
                  能耗: {route.energyConsumption}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AirRouteCard;
